import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { selectSimplifiedEmployees } from '../../../modules/employees.js';
import Empty from '../../Empty.jsx';
import {
  loadingContent,
  loadingReportContent,
  noResultsWithProjectsDescContent,
} from '../../EmptyStates.jsx';
import ReportResultsOverview from './ReportResultsOverview.jsx';
import { selectOrgStakeholdersWithArchived } from '../../../modules/orgStakeholders.js';
import MilestonesModal from './MilestonesModal.jsx';
import GanttChartV4 from '../../gantt-chart-v4/index.jsx';
import ReportGroupsResultsSection from './ReportGroupsResultsSection.jsx';
import BarChart from '../../bar-chart/index.jsx';
import {
  selectFlattenedOrgGroups,
  selectOrgGroups,
} from '../../../modules/orgGroups.js';
import { groupResults } from './utils/index.js';
import { selectOrganization } from '../../../modules/organizations.js';
import { selectCurrentOrganizationId } from '../../../modules/auth.js';

const mapStateToProps = (state) => ({
  employees: selectSimplifiedEmployees(state),
  groups: selectFlattenedOrgGroups(state),
  structuredGroups: selectOrgGroups(state),
  orgStakeholders: selectOrgStakeholdersWithArchived(state),
  hasParentPortfolios: selectOrganization(state).hasParentPortfolios,
  apiUrl: state.config.apiUrl,
  organizationId: selectCurrentOrganizationId(state),
});

ReportResults.defaultProps = {
  results: [],
  selectedEmployeeIds: [],
  selectedGroupIds: [],
  selectedStakeholdersIds: [],
};

function ReportResults(props) {
  const [showMilestonesModal, setShowMilestonesModal] = useState(false);
  const [shownMilestones, setShownMilestones] = useState([]);
  const [pieChartData, setPieChartData] = useState([]);
  const {
    slug,
    isSuccessful,
    results,
    structuredGroups,
    projects,
    portfolios,
    barChartData,
    barChartError,
    query,
    reportOptions,
    onUpdateReportOptions,
    ganttRef,
    pieRef,
    hasParentPortfolios,
    apiUrl,
    organizationId,
  } = props;

  const isPie = reportOptions.reportType === 'pie';
  const isGantt = reportOptions.reportType === 'gantt';

  const onShowMilestoneModal = useCallback((milestones) => {
    setShownMilestones(milestones);
    setShowMilestonesModal(true);
  }, []);

  const onHideMilestoneModal = useCallback(() => {
    setShownMilestones([]);
    setShowMilestonesModal(false);
  }, []);

  const resultsGroups = useMemo(
    () => groupResults(props),
    [
      results,
      query,
      props.selectedEmployeeIds,
      props.selectedGroupIds,
      props.selectedStakeholdersIds,
      props.employees,
      props.groups,
      props.orgStakeholders,
    ]
  );

  const pieChartFilteredResult = useMemo(() => {
    if (!isPie) return [];
    if (query.groupIds) return resultsGroups;
    return groupResults({ ...props, groups: structuredGroups });
  }, [isPie, query.groupIds, resultsGroups, props, structuredGroups]);

  const renderTable = () => {
    if (
      results.projects.length === 0 ||
      (isPie && resultsGroups.length === 0)
    ) {
      if (isSuccessful) {
        return <Empty simplified content={noResultsWithProjectsDescContent} />;
      }
      return <Empty simplified content={loadingContent} />;
    }

    return (
      resultsGroups &&
      resultsGroups.map(({ id, title, participantType, projects }) => (
        <ReportGroupsResultsSection
          key={id}
          title={title}
          participantType={participantType}
          results={projects}
          hasParentPortfolios={hasParentPortfolios}
        />
      ))
    );
  };

  const renderChart = () => {
    if (!reportOptions.isVisible) {
      return (
        <div style={{ height: '415px' }}>
          <Empty simplified content={loadingReportContent} />
        </div>
      );
    }
    if (isPie && reportOptions.isVisible) {
      return (
        <ReportResultsOverview
          pieRef={pieRef}
          query={query}
          projectsCount={results.projects.length}
          resultsGroups={pieChartData}
        />
      );
    }
    if (isGantt && reportOptions.isVisible) {
      return (
        <GanttChartV4
          query={query}
          results={results}
          onShowMilestoneModal={onShowMilestoneModal}
          reportOptions={reportOptions}
          onUpdateReportOptions={onUpdateReportOptions}
          ganttRef={ganttRef}
          hasParentPortfolios={hasParentPortfolios}
          apiUrl={apiUrl}
          organizationId={organizationId}
        />
      );
    }
    if (reportOptions.isVisible) {
      return (
        <BarChart
          result={barChartData}
          reportOptions={reportOptions}
          onUpdateReportOptions={onUpdateReportOptions}
          query={query}
          barChartError={barChartError}
        />
      );
    }
  };

  useEffect(() => {
    if (isPie) {
      setPieChartData(pieChartFilteredResult);
    }
  }, [isPie, pieChartFilteredResult]);

  return (
    <React.Fragment>
      {renderChart()}
      {renderTable()}

      <MilestonesModal
        show={showMilestonesModal}
        onHide={onHideMilestoneModal}
        milestones={shownMilestones}
        portfolios={portfolios}
        projects={projects}
        slug={slug}
      />
    </React.Fragment>
  );
}

export default connect(mapStateToProps)(ReportResults);
